import type { Nudge, NudgeKind } from '@cobliteam/hooks/src/useNudge/types';
import { useTranslation } from '@cobliteam/i18n';
import Tag from '@cobliteam/ui/components/Tag';
import Icon from '@cobliteam/ui/icons';
import { NUDGE_SURFACE } from './nudgeSurface';

/**
 * Chrome shared by every nudge: the kind tag and the close button. The body — title, description
 * and CTAs — comes from the nudge's own `render`, so this component never knows which nudge it is
 * drawing. That is the whole point: there is nowhere here to write a per-source special case.
 */
export interface NudgeCardProps {
  nudge: Nudge;
}

const TAG_STYLE: Record<NudgeKind, { color: 'primary' | 'purple'; icon: 'lightbulb' | 'sparkles' }> = {
  suggestion: { color: 'primary', icon: 'lightbulb' },
  recommendation: { color: 'purple', icon: 'sparkles' },
};

const NudgeCard = ({ nudge }: NudgeCardProps) => {
  const { t } = useTranslation('app-header');
  const { color, icon } = TAG_STYLE[nudge.kind];

  return (
    <div data-testid={`nudge-card-${nudge.id}`} className={`flex w-full flex-col gap-[9px] ${NUDGE_SURFACE.shell} ${NUDGE_SURFACE.padding} ${NUDGE_SURFACE.shadow}`}>
      <div className='flex w-full items-center justify-between'>
        <Tag variant='surface' color={color} size='sm' label={nudge.tagLabel} icon={<Icon name={icon} size='xs' />} />
        <button
          type='button'
          onClick={() => nudge.dismiss()}
          aria-label={t('aiRecommendations.close')}
          className={`flex size-6 items-center justify-center rounded-lg ${NUDGE_SURFACE.muted} transition-colors hover:bg-neutral-100`}
        >
          <Icon name='x' size='sm' />
        </button>
      </div>

      {nudge.render()}
    </div>
  );
};

export default NudgeCard;
